import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { extractApiError, runPublicDemoAnalysis } from './apiService';
import { eyebrowSx, heroPanelSx, pageHeadlineSx, sectionCardSx, subtleCopySx, warmPanelSx } from './designSystem';

const DEFAULT_POLICY_RULE =
  'Block violent, sexual, self-harm, gambling, proxy, or entertainment content unless it is clearly educational or staff-directed.';

const guidedCases = [
  {
    id: 'search',
    label: 'Search results',
    current_url: 'https://www.google.com/search?q=water+cycle+video+for+class',
    text:
      'Search results for water cycle video for class. Result 1: Water Cycle Lab Demonstration for middle school students. Result 2: Funny water fails compilation. Result 3: Weather song for kids. The user is looking for a classroom lesson and educational science video.',
    browserTitle: 'Search results for "water cycle video for class"',
    view: 'search',
  },
  {
    id: 'lesson',
    label: 'Assigned lesson',
    current_url: 'https://www.youtube.com/watch?v=teacher-assigned-video',
    text:
      'Water Cycle Lab Demonstration. Classroom science lesson about evaporation, condensation, precipitation, and collection. Teacher assigned educational video for students to watch during class and use in note-taking.',
    browserTitle: 'Water Cycle Lab Demonstration',
    view: 'lesson',
  },
  {
    id: 'weather-song',
    label: 'Educational song',
    current_url: 'https://www.youtube.com/watch?v=weather-song-kids',
    text:
      "Weather Song for Kids. Elementary classroom learning video about weather vocabulary, seasons, clouds, rain, and sunshine. Teacher-approved educational children's media.",
    browserTitle: 'Weather Song for Kids',
    view: 'lesson',
  },
  {
    id: 'funny-fails',
    label: 'Funny water fails',
    current_url: 'https://www.youtube.com/watch?v=funny-water-fails',
    text:
      'Funny water fails compilation, pranks, embarrassing accidents, viral entertainment clips, laughter-focused montage, unrelated to classroom instruction.',
    browserTitle: 'Funny Water Fails Compilation',
    view: 'blocked',
  },
  {
    id: 'prank-video',
    label: 'Prank compilation',
    current_url: 'https://www.youtube.com/watch?v=prank-compilation',
    text:
      'Prank compilation, viral jokes, hidden camera clips, attention-grabbing comedy videos, entertainment-focused recommendations after a science lesson.',
    browserTitle: 'Prank Compilation',
    view: 'blocked',
  },
  {
    id: 'celeb-drama',
    label: 'Celebrity drama',
    current_url: 'https://www.youtube.com/watch?v=celebrity-drama-recap',
    text:
      'Celebrity drama recap, gossip, social-media controversy coverage, entertainment news, sensational updates unrelated to school instruction.',
    browserTitle: 'Celebrity Drama Recap',
    view: 'blocked',
  },
  {
    id: 'gaming-highlights',
    label: 'Gaming livestream',
    current_url: 'https://www.youtube.com/watch?v=gaming-highlights',
    text:
      'Gaming livestream highlights, streamer reactions, multiplayer gameplay recap, trending entertainment for student audiences, unrelated to educational classwork.',
    browserTitle: 'Gaming Livestream Highlights',
    view: 'blocked',
  },
];

const browserSteps = [
  { id: 'search', short: '1', title: 'Search' },
  { id: 'lesson', short: '2', title: 'Lesson' },
  { id: 'blocked', short: '3', title: 'Blocked click' },
];

function DemoPage() {
  const [policyRule, setPolicyRule] = useState(DEFAULT_POLICY_RULE);
  const [debouncedPolicyRule, setDebouncedPolicyRule] = useState(DEFAULT_POLICY_RULE);
  const [activeCaseId, setActiveCaseId] = useState(guidedCases[0].id);
  const [guidedResults, setGuidedResults] = useState({});
  const [guidedLoading, setGuidedLoading] = useState(false);
  const [guidedError, setGuidedError] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [customResult, setCustomResult] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [customError, setCustomError] = useState('');

  const activeCase = useMemo(
    () => guidedCases.find((item) => item.id === activeCaseId) || guidedCases[0],
    [activeCaseId]
  );

  const activeResult =
    guidedResults[activeCase.id]?._policyRule === debouncedPolicyRule ? guidedResults[activeCase.id] : null;

  const setCase = (caseId) => setActiveCaseId(caseId);

  const runGuidedAnalysis = async (demoCase, policyOverride = debouncedPolicyRule) => {
    try {
      setGuidedLoading(true);
      setGuidedError('');
      const result = await runPublicDemoAnalysis({
        current_url: demoCase.current_url,
        policy_rule: policyOverride,
        text: demoCase.text,
        fetch_live: false,
      });
      setGuidedResults((current) => ({
        ...current,
        [demoCase.id]: { ...result, _policyRule: policyOverride },
      }));
    } catch (error) {
      setGuidedError(extractApiError(error, 'Unable to run the guided demo right now.'));
    } finally {
      setGuidedLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedPolicyRule(policyRule.trim());
    }, 650);

    return () => window.clearTimeout(timeoutId);
  }, [policyRule]);

  useEffect(() => {
    if (!debouncedPolicyRule) {
      return;
    }

    if (!guidedResults[activeCase.id] || guidedResults[activeCase.id]?._policyRule !== debouncedPolicyRule) {
      runGuidedAnalysis(activeCase, debouncedPolicyRule);
    }
  }, [activeCase, debouncedPolicyRule]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRunCustomUrl = async () => {
    try {
      setCustomLoading(true);
      setCustomError('');
      setCustomResult(null);
      const result = await runPublicDemoAnalysis({
        current_url: customUrl,
        policy_rule: policyRule,
        fetch_live: true,
      });
      setCustomResult(result);
    } catch (error) {
      setCustomError(extractApiError(error, 'Unable to analyze that URL right now.'));
    } finally {
      setCustomLoading(false);
    }
  };

  const renderSearchBrowser = () => (
    <Box sx={{ p: 1.8 }}>
      <Box
        sx={{
          p: 1.05,
          borderRadius: '12px',
          border: '1px solid rgba(11,46,67,0.1)',
          bgcolor: 'white',
          color: '#52677A',
          fontSize: '0.8rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.35,
        }}
      >
        <SearchOutlinedIcon sx={{ fontSize: 18, color: '#52677A' }} />
        water cycle video for class
      </Box>
      <Stack spacing={1.05}>
        {[
          {
            title: 'Water Cycle Lab Demonstration for students',
            domain: 'youtube.com/watch?v=teacher-assigned-video',
            hint: 'Teacher-assigned science lesson',
            target: 'lesson',
            tone: 'allow',
          },
          {
            title: 'Funny Water Fails Compilation',
            domain: 'youtube.com/watch?v=funny-water-fails',
            hint: 'Entertainment result',
            target: 'funny-fails',
            tone: 'block',
          },
          {
            title: 'Weather Song for Kids',
            domain: 'youtube.com/watch?v=weather-song-kids',
            hint: 'Educational alternative',
            target: 'weather-song',
            tone: 'allow',
          },
        ].map((item) => (
          <Box
            key={item.title}
            onClick={() => setCase(item.target)}
            sx={{
              p: 1.3,
              borderRadius: '14px',
              border:
                item.tone === 'allow'
                  ? '1px solid rgba(19,148,127,0.18)'
                  : '1px solid rgba(192,69,49,0.16)',
              bgcolor: 'white',
              cursor: 'pointer',
              transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 12px 24px rgba(11,46,67,0.08)',
                borderColor:
                  item.tone === 'allow' ? 'rgba(19,148,127,0.28)' : 'rgba(192,69,49,0.24)',
              },
            }}
          >
            <Typography sx={{ color: '#0B2E43', fontWeight: 800, fontSize: '0.84rem' }}>{item.title}</Typography>
            <Typography sx={{ color: item.tone === 'allow' ? '#13947F' : '#B2604C', fontSize: '0.72rem', mt: 0.25 }}>
              {item.domain}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} alignItems="center">
              <Chip
                size="small"
                label={item.tone === 'allow' ? 'Open lesson' : 'Open distracting result'}
                sx={{
                  bgcolor: item.tone === 'allow' ? 'rgba(19,148,127,0.12)' : 'rgba(192,69,49,0.12)',
                  color: item.tone === 'allow' ? '#0C6E5E' : '#9A3322',
                  fontWeight: 800,
                }}
              />
              <Typography sx={{ color: '#7A8FA0', fontSize: '0.78rem' }}>{item.hint}</Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );

  const renderLessonBrowser = (blocked) => (
    <Grid container sx={{ minHeight: 338 }}>
      <Grid item xs={12} md={7.3} sx={{ p: 2.1, bgcolor: 'white' }}>
        <Box
          sx={{
            aspectRatio: '16 / 9',
            borderRadius: '18px',
            overflow: 'hidden',
            position: 'relative',
            background: blocked
              ? 'linear-gradient(135deg, #180b0a 0%, #30140f 100%)'
              : 'linear-gradient(135deg, #0c2f43 0%, #175f7d 52%, #217b69 100%)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          {!blocked && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
              <Box
                sx={{
                  width: 62,
                  height: 62,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  backdropFilter: 'blur(8px)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 0,
                    height: 0,
                    borderTop: '11px solid transparent',
                    borderBottom: '11px solid transparent',
                    borderLeft: '17px solid white',
                    ml: '4px',
                  }}
                />
              </Box>
            </Box>
          )}
          {blocked && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(0,0,0,0.56)',
                backdropFilter: 'blur(7px)',
              }}
            >
              <Stack alignItems="center" spacing={1.1}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'rgba(192,69,49,0.2)',
                    border: '1px solid rgba(192,69,49,0.42)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f08d78" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </Box>
                <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.92rem' }}>Page blocked by school policy</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.46)', fontSize: '0.74rem' }}>
                  Recommendation does not fit instructional use
                </Typography>
              </Stack>
            </Box>
          )}
          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: 1.45, background: 'linear-gradient(transparent, rgba(0,0,0,0.78))' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Current page
            </Typography>
            <Typography sx={{ color: 'white', fontWeight: 700, mt: 0.35, fontSize: '0.86rem' }}>{activeCase.browserTitle}</Typography>
          </Box>
        </Box>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mt: 1.35 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#0B2E43', fontWeight: 800, fontSize: '0.88rem', lineHeight: 1.34 }}>
              {blocked ? 'Prank Compilation' : activeCase.browserTitle}
            </Typography>
            <Typography sx={{ mt: 0.45, color: '#667f8d', fontSize: '0.78rem', lineHeight: 1.58 }}>
              {blocked
                ? 'Entertainment content, viral clips, and unrelated recommendations.'
                : 'Teacher-directed instructional page stays available for class use.'}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={blocked ? 'Blocked' : 'Allowed'}
            sx={
              blocked
                ? { bgcolor: 'rgba(192,69,49,0.12)', color: '#9A3322', fontWeight: 800, flexShrink: 0 }
                : { bgcolor: 'rgba(19,148,127,0.12)', color: '#0C6E5E', fontWeight: 800, flexShrink: 0 }
            }
          />
        </Stack>
      </Grid>

      <Grid item xs={12} md={4.7} sx={{ p: 1.9, bgcolor: '#f4f8fa', borderLeft: '1px solid rgba(11,46,67,0.08)' }}>
        <Box
          sx={{
            p: 1.15,
            borderRadius: '12px',
            bgcolor: 'rgba(19,148,127,0.08)',
            border: '1px solid rgba(19,148,127,0.18)',
            mb: 1.35,
          }}
        >
          <Typography sx={{ color: '#13947F', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.45 }}>
            Policy in effect
          </Typography>
          <Typography sx={{ color: '#3B6170', fontSize: '0.72rem', lineHeight: 1.5 }}>
            Block entertainment unless clearly educational or staff-directed.
          </Typography>
        </Box>

        <Typography sx={{ color: '#8CA3B2', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.75 }}>
          Up Next
        </Typography>
        <Stack spacing={0.78}>
          {[
            { label: 'Prank Compilation', target: 'prank-video' },
            { label: 'Celebrity Drama Recap', target: 'celeb-drama' },
            { label: 'Gaming Livestream Highlights', target: 'gaming-highlights' },
          ].map((item, index) => {
            const isPrimaryBlocked = blocked && index === 0;
            return (
              <Box
                key={item.label}
                onClick={() => setCase(item.target)}
                sx={{
                  p: 0.96,
                  borderRadius: '10px',
                  bgcolor: 'white',
                  border: isPrimaryBlocked ? '1px solid rgba(192,69,49,0.26)' : '1px solid rgba(11,46,67,0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 180ms ease, box-shadow 180ms ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 10px 20px rgba(11,46,67,0.08)',
                  },
                }}
              >
                <Typography sx={{ color: '#6b8290', fontWeight: 700, fontSize: '0.72rem', filter: isPrimaryBlocked ? 'blur(2.4px)' : 'none' }}>
                  {item.label}
                </Typography>
                {!isPrimaryBlocked && (
                  <Typography sx={{ color: '#8CA3B2', fontSize: '0.7rem', mt: 0.35 }}>Click to follow recommendation</Typography>
                )}
                {isPrimaryBlocked && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,0.88)' }}>
                    <Typography sx={{ color: '#9A3322', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Blocked
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Grid>
    </Grid>
  );

  const renderGuidedBrowserBody = () => {
    if (!activeResult) {
      return (
        <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center' }}>
          <Stack spacing={1.4} alignItems="center">
            <CircularProgress size={30} />
            <Typography sx={{ color: '#52677A' }}>Running live policy check...</Typography>
          </Stack>
        </Box>
      );
    }

    if (activeResult.result) {
      return renderLessonBrowser(true);
    }

    if (activeCase.view === 'search') {
      return renderSearchBrowser();
    }

    return renderLessonBrowser(false);
  };

  return (
    <Box sx={{ color: '#0B2E43' }}>
      <Container sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={4}>
          <Box sx={{ ...heroPanelSx, p: { xs: 3.5, md: 6 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="overline" sx={eyebrowSx}>
                  Live Demo
                </Typography>
                <Typography variant="h2" sx={{ ...pageHeadlineSx, mt: 1.5, maxWidth: 760 }}>
                  See the filtering flow the way a school admin would explain it.
                </Typography>
                <Typography sx={{ ...subtleCopySx, mt: 2, maxWidth: 620, fontSize: '1.02rem' }}>
                  Edit the policy below, click through a simulated student session, and watch the model decide page by page.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ ...warmPanelSx, p: 2.75 }}>
                  <Stack spacing={1.1}>
                    <Stack direction="row" spacing={1.1} alignItems="center">
                      <GavelOutlinedIcon sx={{ color: '#A8771F' }} />
                      <Typography sx={{ color: '#8C6B1F', fontWeight: 800 }}>Real model, real decisions</Typography>
                    </Stack>
                    <Typography sx={{ color: '#52677A', fontSize: '0.9rem', lineHeight: 1.65 }}>
                      Every verdict on this page comes from the same AI policy engine schools deploy — nothing is staged.
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={4.2}>
              <Stack spacing={3}>
                <Card sx={sectionCardSx}>
                  <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                    <Typography variant="h5" sx={{ color: '#0B2E43' }}>
                      Demo policy
                    </Typography>
                    <TextField
                      multiline
                      minRows={5}
                      fullWidth
                      value={policyRule}
                      onChange={(event) => setPolicyRule(event.target.value)}
                      sx={{ mt: 1.6 }}
                    />
                  </CardContent>
                </Card>

                <Box sx={{ ...warmPanelSx, p: 2.5 }}>
                  <Stack spacing={1.1}>
                    <Stack direction="row" spacing={1.1} alignItems="center">
                      <VisibilityOutlinedIcon sx={{ color: '#A8771F' }} />
                      <Typography sx={{ color: '#8C6B1F', fontWeight: 800 }}>Use the browser</Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row', lg: 'column', xl: 'row' }} spacing={1.25} sx={{ pt: 0.5 }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setActiveCaseId(guidedCases[0].id);
                          runGuidedAnalysis(guidedCases[0]);
                        }}
                        disabled={guidedLoading}
                      >
                        Restart walkthrough
                      </Button>
                      <Button variant="text" onClick={() => runGuidedAnalysis(activeCase)} disabled={guidedLoading}>
                        Rerun current screen
                      </Button>
                    </Stack>
                  </Stack>
                  {guidedError && (
                    <Alert severity="error" sx={{ mt: 1.8 }}>
                      {guidedError}
                    </Alert>
                  )}
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={7.8}>
              <Card sx={sectionCardSx}>
                <CardContent sx={{ p: { xs: 2.4, md: 2.8 } }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.1} sx={{ mb: 1.6 }}>
                    {browserSteps.map((step) => {
                      const active =
                        step.id === 'search'
                          ? activeCase.view === 'search'
                          : step.id === 'lesson'
                            ? activeCase.view === 'lesson'
                            : activeCase.view === 'blocked';

                      return (
                        <Box
                          key={step.id}
                          sx={{
                            flex: 1,
                            p: 1.2,
                            borderRadius: '14px',
                            border: active ? '1px solid rgba(19,148,127,0.22)' : '1px solid rgba(11,46,67,0.08)',
                            bgcolor: active ? 'rgba(19,148,127,0.06)' : 'rgba(247,250,248,0.75)',
                          }}
                        >
                          <Typography sx={{ color: active ? '#13947F' : '#7A8FA0', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.08em' }}>
                            STEP {step.short}
                          </Typography>
                          <Typography sx={{ mt: 0.35, color: '#0B2E43', fontWeight: 800 }}>{step.title}</Typography>
                        </Box>
                      );
                    })}
                  </Stack>

                  <Box
                    sx={{
                      borderRadius: '22px',
                      overflow: 'hidden',
                      border: '1px solid rgba(11,46,67,0.08)',
                      bgcolor: '#f7fafc',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.2,
                        borderBottom: '1px solid rgba(11,46,67,0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.2,
                        bgcolor: '#eef3f5',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.7 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef8f7f' }} />
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e6c15f' }} />
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#68c08e' }} />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          px: 1.45,
                          py: 0.88,
                          borderRadius: '999px',
                          bgcolor: 'white',
                          color: '#6b8290',
                          fontSize: '0.82rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {activeCase.current_url}
                      </Box>
                      {activeResult && (
                        <Chip
                          label={activeResult.result ? 'Blocked' : 'Allowed'}
                          size="small"
                          sx={
                            activeResult.result
                              ? { bgcolor: 'rgba(192,69,49,0.14)', color: '#9A3322', fontWeight: 800 }
                              : { bgcolor: 'rgba(19,148,127,0.12)', color: '#0C6E5E', fontWeight: 800 }
                          }
                        />
                      )}
                    </Box>

                    {renderGuidedBrowserBody()}
                  </Box>

                  {activeResult && (
                    <Box
                      sx={{
                        mt: 1.6,
                        p: 2,
                        borderRadius: '18px',
                        border: '1px solid rgba(11,46,67,0.08)',
                        backgroundColor: 'rgba(247,250,248,0.92)',
                      }}
                    >
                      <Typography sx={{ color: '#0B2E43', fontWeight: 800 }}>Why the model decided that</Typography>
                      <Typography sx={{ mt: 0.85, color: '#52677A', lineHeight: 1.75 }}>
                        {activeResult.explanation}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={sectionCardSx}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={5}>
                  <Typography variant="h4" sx={{ color: '#0B2E43' }}>
                    Try any URL
                  </Typography>
                  <TextField
                    label="URL to analyze"
                    fullWidth
                    value={customUrl}
                    onChange={(event) => setCustomUrl(event.target.value)}
                    sx={{ mt: 2.2 }}
                    placeholder="https://example.com/article"
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
                    <Button variant="contained" onClick={handleRunCustomUrl} disabled={customLoading || !customUrl.trim()}>
                      {customLoading ? 'Analyzing...' : 'Run live URL demo'}
                    </Button>
                    <Button variant="text" component={RouterLink} to="/">
                      Back to homepage
                    </Button>
                  </Stack>
                  {customError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {customError}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={7}>
                  <Box
                    sx={{
                      minHeight: 270,
                      borderRadius: '22px',
                      border: '1px solid rgba(11,46,67,0.08)',
                      bgcolor: '#f8fbfc',
                      p: 2.2,
                    }}
                  >
                    {!customResult && !customLoading && (
                      <Stack spacing={1.2} sx={{ color: '#52677A' }}>
                        <Typography variant="h6" sx={{ color: '#0B2E43' }}>
                          Live demo results will appear here
                        </Typography>
                      </Stack>
                    )}

                    {customLoading && (
                      <Stack spacing={1.4} alignItems="center" justifyContent="center" sx={{ minHeight: 220 }}>
                        <CircularProgress size={30} />
                        <Typography sx={{ color: '#52677A' }}>Fetching page and running the live model...</Typography>
                      </Stack>
                    )}

                    {customResult && (
                      <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.4}>
                          <Box>
                            <Typography variant="h6" sx={{ color: '#0B2E43' }}>
                              {customResult.current_url}
                            </Typography>
                            <Typography sx={{ color: '#52677A', mt: 0.35 }}>
                              Provider: {customResult.provider} • Domain: {customResult.domain}
                            </Typography>
                          </Box>
                          <Chip
                            label={customResult.result ? 'Blocked' : 'Allowed'}
                            sx={
                              customResult.result
                                ? { alignSelf: 'flex-start', fontWeight: 800, bgcolor: 'rgba(192,69,49,0.16)', color: '#9A3322' }
                                : { alignSelf: 'flex-start', fontWeight: 800, bgcolor: 'rgba(19,148,127,0.14)', color: '#0C6E5E' }
                            }
                          />
                        </Stack>

                        <Alert severity={customResult.result ? 'warning' : 'success'}>
                          {customResult.explanation}
                        </Alert>

                        <Box sx={{ p: 1.6, borderRadius: '16px', bgcolor: 'white', border: '1px solid rgba(11,46,67,0.08)' }}>
                          <Typography sx={{ color: '#52677A', fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                            Extracted page preview
                          </Typography>
                          <Typography sx={{ mt: 0.95, color: '#52677A', lineHeight: 1.7 }}>
                            {customResult.content_preview}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box sx={{ ...warmPanelSx, p: { xs: 2.6, md: 3.5 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box>
                <Typography sx={{ color: '#8C6B1F', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  Liked what it decided?
                </Typography>
                <Typography variant="h5" sx={{ color: '#0B2E43', mt: 0.75 }}>
                  See how the same engine runs across a whole school.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} flexShrink={0}>
                <Button variant="contained" color="primary" component={RouterLink} to="/info" endIcon={<OpenInNewOutlinedIcon />}>
                  Explore Product
                </Button>
                <Button variant="outlined" component={RouterLink} to="/setup">
                  See Rollout Guide
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

export default DemoPage;
